import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BuilderIOTabOptions } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";

import { useCMSTabContext } from "../cms-tab-context";

const cmsBuilderTabFormSchema = z.object({
  builder_io_path: z.string().min(1, "Builder.io Path is required"),
  builder_io_api_key: z.string().min(1, "Builder.io API key is required"),
  sync_with_tamper_status: z.boolean(),
});

type Props = {
  options: BuilderIOTabOptions;
};

export const BuilderIOTabSettings: FC<Props> = ({ options }) => {
  const form = useForm<z.infer<typeof cmsBuilderTabFormSchema>>({
    resolver: zodResolver(cmsBuilderTabFormSchema),
    defaultValues: {
      builder_io_path: get(options, "builder_io_path", ""),
      builder_io_api_key: get(options, "builder_io_api_key", ""),
      sync_with_tamper_status: get(options, "sync_with_tamper_status", false),
    },
    mode: "onChange",
  });

  const { tabIndex } = useCMSTabContext();
  const onUpdate = useCMSStore.use.onUpdateBuilderIOTabOptions();

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onUpdate(tabIndex, values);
      },
    });

    return unsubscribe;
  }, [form, options, tabIndex, onUpdate]);

  return (
    <Card>
      <CardHeader>
        <h4 className="text-lg font-medium leading-none">Settings</h4>
      </CardHeader>
      <Form {...form}>
        <CardContent className="text-sm space-y-4">
          <FormField
            control={form.control}
            name="builder_io_path"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <div className="flex justify-between items-center mb-0">
                  <FormLabel>Builder.io Path</FormLabel>
                  <a
                    href="https://www.builder.io/c/docs/targeting#target-options-by-plan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground underline"
                  >
                    Where to find the Builder.io path
                  </a>
                </div>
                <FormControl>
                  <Input
                    placeholder="This is the builder.io path for the embedded content."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="builder_io_api_key"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <div className="flex justify-between items-center mb-0">
                  <FormLabel>Builder.io API Key</FormLabel>
                  <a
                    href="https://www.builder.io/c/docs/using-your-api-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground underline"
                  >
                    Where to find the Builder.io API key
                  </a>
                </div>
                <FormControl>
                  <Input
                    placeholder="The API key can be found in the Environments section of the Settings page."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sync_with_tamper_status"
            render={({ field }) => (
              <FormItem className="flex justify-between items-center">
                <div className="space-y-1.5">
                  <FormLabel>Sync with Tamper Tag</FormLabel>
                  <FormDescription>
                    Lock the contents of this tab until a consumer breaks the
                    seal of the tamper tag.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </CardContent>
      </Form>
    </Card>
  );
};
